import { useEffect, useRef, useState } from "react";

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Pages are processed in ordered batches: enough parallelism to be quick,
// while still discovering matches front-to-back so the first hit is the
// earliest page in the document.
const BATCH_SIZE = 4;

const IDLE_PROGRESS = { done: 0, total: 0, isSearching: false };

/**
 * Finds the pages of an already-open PDF whose text matches a search term.
 *
 * Deliberately extracts nothing until there is something to search for.
 * Eagerly walking every page on open is what forced whole documents to
 * download before the user had done anything.
 *
 * Takes a PDFDocumentProxy rather than a URL so it shares the transport the
 * viewer already opened — react-pdf hands the proxy to <Document onLoadSuccess>
 * — instead of starting a second download of the same file.
 *
 * @param {import("pdfjs-dist").PDFDocumentProxy|null} pdfDoc
 * @param {string} searchString
 * @returns {{ matchingPages: number[], progress: { done: number, total: number, isSearching: boolean } }}
 */
export const usePdfTextExtraction = (pdfDoc, searchString) => {
  // Page text survives across searches for the same document, so only the
  // first search pays for extraction.
  const cacheRef = useRef({ pdfDoc: null, texts: [] });
  const [matchingPages, setMatchingPages] = useState([]);
  const [progress, setProgress] = useState(IDLE_PROGRESS);

  useEffect(() => {
    const term = searchString ? searchString.trim() : "";

    if (!pdfDoc || !term) {
      setMatchingPages([]);
      setProgress(IDLE_PROGRESS);
      return;
    }

    if (cacheRef.current.pdfDoc !== pdfDoc) {
      cacheRef.current = { pdfDoc, texts: [] };
    }
    const { texts } = cacheRef.current;

    const total = pdfDoc.numPages;
    const regex = new RegExp(escapeRegExp(term), "i");

    let cancelled = false;
    const matches = [];

    const readPage = async (pageNumber) => {
      if (texts[pageNumber] !== undefined) return texts[pageNumber];

      const page = await pdfDoc.getPage(pageNumber);
      try {
        const content = await page.getTextContent();
        return content.items.map((item) => item.str).join(" ");
      } finally {
        // Releases the page's operator list. pdf.js defers this while a render
        // task is in flight, so it is safe for pages the viewer is showing.
        page.cleanup();
      }
    };

    const run = async () => {
      setMatchingPages([]);
      setProgress({ done: 0, total, isSearching: true });

      for (let start = 1; start <= total; start += BATCH_SIZE) {
        const batch = [];
        for (let n = start; n < start + BATCH_SIZE && n <= total; n += 1) batch.push(n);

        const batchTexts = await Promise.all(batch.map(readPage));
        if (cancelled) return;

        let foundInBatch = false;
        batch.forEach((pageNumber, index) => {
          texts[pageNumber] = batchTexts[index];
          if (regex.test(batchTexts[index])) {
            matches.push(pageNumber);
            foundInBatch = true;
          }
        });

        if (foundInBatch) setMatchingPages([...matches]);
        const done = Math.min(start + BATCH_SIZE - 1, total);
        setProgress({ done, total, isSearching: done < total });
      }

      if (!cancelled) setProgress({ done: total, total, isSearching: false });
    };

    run().catch((error) => {
      // A closed modal destroys the document mid-walk; that is expected.
      if (cancelled) return;
      console.error("PDF text extraction failed", error);
      setProgress(IDLE_PROGRESS);
    });

    return () => {
      cancelled = true;
    };
  }, [pdfDoc, searchString]);

  return { matchingPages, progress };
};

export default usePdfTextExtraction;
